import { DestroyRef, Directive, ElementRef, inject, input, NgZone, output } from '@angular/core';

/**
 * How long a comment must stay on screen before it counts as read. Short enough
 * that reading a thread clears it, long enough that a fast scroll past does not.
 */
export const CRL_COMMENT_DWELL_MS = 2000;

/**
 * Emits its comment's id once that comment has been rendered AND continuously on
 * screen for {@link CRL_COMMENT_DWELL_MS}. Leaving the viewport before then
 * cancels the pending emission, and a comment the page never rendered never
 * carries the directive at all — so it can never be reported.
 *
 * One `IntersectionObserver` per rendered comment; both top-level comments and
 * replies carry it, since either can be unread.
 */
@Directive({ selector: '[cxpCrlCommentDwell]' })
export class CrlCommentDwellDirective {
  /** Id of the comment this element renders. */
  readonly cxpCrlCommentDwell = input.required<string>();
  /** Fires once, when the dwell time elapses. */
  readonly dwelled = output<string>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private timer: ReturnType<typeof setTimeout> | null = null;
  private hasDwelled = false;

  constructor() {
    const observer = new IntersectionObserver((entries) => this.onIntersection(entries));
    observer.observe(this.host.nativeElement);

    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      this.clearTimer();
    });
  }

  private onIntersection(entries: IntersectionObserverEntry[]): void {
    const isVisible = entries.some((entry) => entry.isIntersecting);

    if (!isVisible) {
      this.clearTimer();
      return;
    }
    if (this.hasDwelled || this.timer !== null) return;

    this.timer = setTimeout(() => {
      this.timer = null;
      this.hasDwelled = true;
      // The observer callback is not zone-patched, so the emission (and the write
      // it drives) would otherwise land outside Angular's change detection.
      this.zone.run(() => this.dwelled.emit(this.cxpCrlCommentDwell()));
    }, CRL_COMMENT_DWELL_MS);
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }
}
